<script setup lang="ts">
    import { ref } from 'vue';
    import { formatAddress } from '@/helpers/formatAddress';
    import { useModalStore } from '@/stores/modals';
    import { useBookingStore } from '@/stores/booking';
    import { fetchAndStoreAvailability } from '~/helpers/fetchData';
    import { useAvailabilityStore } from '~/stores/booking-availability';
    import { useErrorStore } from '~/stores/error';
    import { useRuntimeConfig } from 'nuxt/app';
    import { computed } from 'vue';
    import { getNearByGymLocations } from '~/helpers/geoLocation';
    import { nearByGymLocationSearchRadius, visibleNearByGymsLimit } from '~/helpers/constants';
    import { fetchStandardPackages, resetStandardPackages } from '~/helpers/fetchStandardPackages';

    // props
    const props = defineProps<{
        allGyms: any[];
    }>();

    // stores
    const modalStore = useModalStore();
    const bookingStore = useBookingStore();
    const availabilityStore = useAvailabilityStore();
    const errorStore = useErrorStore();
    const runtimeConfig = useRuntimeConfig();

    // local state
    const selectedGymData = ref<any>(null);
    const gymsWithFormattedAddress = computed(() =>
        props.allGyms.map((gym) => ({
            ...gym,
            formattedAddress: formatAddress(gym.address),
        }))
    );
    const switchToGymName = ref(bookingStore.state.display_name || '');

    // methods
    const switchToGym = (gym: any, gym_name: string,) => {
        selectedGymData.value = gym;
         switchToGymName.value = gym_name;
    };
    const selectedGym = ref(bookingStore.state.display_name || '');

    const navigateToSelectedGym = async () => {
        availabilityStore.resetAvailability();
        resetStandardPackages();
        modalStore.updateModal('locationModal', false);
        const gym = selectedGymData.value;

        const nearByLocations = getNearByGymLocations(gym, props.allGyms, nearByGymLocationSearchRadius);

        bookingStore.updateBookingState({
            display_name: gym.location_name,
            currency: gym.currency,
            address: gym.address,
            booking_business: gym.booking_business,
            service_id: gym.service_id,
            timezone: gym.timezone,
            timezone_name: gym.timezone_name,
            gym_switched: true,
            is_loader: true,
            country_code: selectedGymData.value?.country_code ?? bookingStore.state.country_code,
            maps_url: gym.maps_url,
            near_by_location: nearByLocations.slice(0, visibleNearByGymsLimit),
        });

        if (gym.is_pricing_panel_enabled) await fetchStandardPackages(gym.location_id);

        await fetchAndStoreAvailability(
            bookingStore,
            availabilityStore,
            errorStore,
            runtimeConfig,
            switchToGymName.value || bookingStore.state.display_name,
            true
        );
        bookingStore.updateBookingState({
            is_loader: false,
        });
    };

    const closeLocationsModal = () => {
        modalStore.updateModal('locationModal', false);
        switchToGymName.value = '';
    };
</script>

<template>
    <!-- MODAL HEADER BLOCK -->
    <div class="modal-location-header">
        <div class="flex flex-row justify-between items-start">
            <h5 class="text-4xl font-bold pr-5">All Locations</h5>
            <button
                class="bg-black/5 p-3 rounded-full -mt-[10px] -mr-[10px] md:-mt-[20px] md:-mr-[20px]"
                @click="closeLocationsModal">
                <Icon
                    class="!w-[24px] pointer-events-none !h-[24px] block mx-auto"
                    icon="close"
                    alt="U.P Location Icon" />
            </button>
        </div>
        <div class="pt-8 md:pt-6">
            <h4 class="text-xl font-bold">Select a location *</h4>
            <p class="location-city-subtext text-sm text-[#888b8d]">
                View consultation availability at all U.P. locations.
            </p>
        </div>
    </div>
    <!-- MODAL CONTENT BLOCK -->
    <div
        class="modal-location-content my-8 md:mt-6 flex-1 overflow-auto custom-scroll">
        <ul>
            <li
                v-for="gym in gymsWithFormattedAddress"
                :key="gym.location_id">
                <label class="flex flex-row items-center">
                    <input
                        type="radio"
                        :value="gym.location_name"
                        v-model="selectedGym"
                        name="selectGym"
                        @change="switchToGym(gym, gym.location_name)"
                        class="hidden" />
                    <div class="special-request-radio up-checkbox"></div>
                    <div class="ml-4 mr-2 border-b border-black/10 grow py-2">
                        <h6>{{ gym.location_name }}</h6>
                        <span class="text-sm text-[#888b8d]">
                            {{ gym.formattedAddress }}
                        </span>
                    </div>
                </label>
            </li>
        </ul>
    </div>
    <!-- MODAL FOOTER BLOCK -->
    <div class="modal-location-footer flex">
        <button
            class="rounded-full py-3 px-6 grow text-xl"
            :class="[
                !switchToGymName
                    ? 'bg-[rgba(1,1,1,0.1)] pointer-events-none'
                    : 'bg-[#161412]',
            ]"
            @click="navigateToSelectedGym"
            :disabled="!switchToGymName">
            <div
                :class="[
                    !switchToGymName
                        ? 'text-[#b2b2b2] pointer-events-none'
                        : 'text-[#fff]',
                ]">
                VIEW AVAILABILITY
            </div>
        </button>
    </div>
</template>

<style scoped>
    .up-checkbox {
        border: 2px solid rgba(0, 0, 0, 0.2);
    }
    .up-checkbox:before {
        background-color: #efede7;
    }

    input[type='radio']:checked + .up-checkbox {
        border: 2px solid #161412;
        background-color: #efede7;
    }
    input[type='radio']:checked + .up-checkbox:before {
        background-color: #161412;
    }

    .custom-scroll::-webkit-scrollbar {
        width: 6px;
    }

    .custom-scroll::-webkit-scrollbar-track {
        background: transparent;
    }

    .custom-scroll::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.2);
        border-radius: 9999px;
    }

    .custom-scroll::-webkit-scrollbar-thumb:hover {
        background-color: rgba(0, 0, 0, 0.4);
    }
</style>
